import { format } from "date-fns";
import { ReactNode } from "react";
import { graphql, useFragment } from "react-relay";
import { cn } from "src/common/utils";
import { TruncatedMarkdown, TruncatedText } from "src/components/TruncatedText";
import { Markdown } from "src/components/ui/Markdown";
import { Shimmer } from "src/components/ui/shimmer";
import { InteractiveAlertTag } from "src/pages/AlertRespondersPage/components/InteractiveAlertTag";

import { JobRunHeader_jobRun$key } from "./__generated__/JobRunHeader_jobRun.graphql";

export interface TagStatsType {
  categories: Record<string, { count: number; mostRecent: Date }>;
  services: Record<string, { count: number; mostRecent: Date }>;
}


// Define the fragment that specifies the data this component needs
export const jobRunHeaderFragment = graphql`
  fragment JobRunHeader_jobRun on GraphQLJobRun {
    id
    name
    createdAt
    updatedAt
    status
    outputJson
    alertEvent {
      alertDescription
    }
    job {
      id
      name
    }
    investigation {
      id
      summary
      services
      category
    }
    digest {
      id
      summary
    }
  }
`;

export interface JobRunHeaderProps {
  /** The job run data */
  jobRun: JobRunHeader_jobRun$key;
  /** Optional content to display on the right side */
  rightContent?: ReactNode;
  /** Whether to show the job run title */
  showTitle?: boolean;
  /** Whether to show text match */
  showTextMatch?: boolean;
  /** Context for display (affects label for created date) */
  context?: "alert" | "digest";
  /** Layout variant - "timeline-card" uses compact two-column layout */
  variant?: "default" | "timeline-card";
  /** Handler for tag clicks (enables interactive tags) */
  onTagClick?: (type: "category" | "service", value: string) => void;
  /** Active filter state for highlighting active tags */
  activeFilters?: { categories: Set<string>; services: Set<string> };
  /** Statistics for tags (count, most recent) */
  tagStats?: TagStatsType;
}

const getUtcLocalTimes = (isoDate: string | null | undefined): { utc: string; local: string } | null => {
  if (!isoDate) {
    return null;
  }

  const date = new Date(isoDate);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);

  return {
    utc: format(utcDate, "MMM d, HH:mm"),
    local: format(date, "MMM d, HH:mm"),
  };
};

export function JobRunHeader({
  jobRun,
  rightContent,
  showTitle = true,
  showTextMatch = true,
  context,
  variant = "default",
  onTagClick,
  activeFilters,
  tagStats,
}: JobRunHeaderProps) {
  // Use fragment to extract data
  const data = useFragment(jobRunHeaderFragment, jobRun);

  // Extract data from fragment
  const title = data.name || "";
  const createdAt = new Date(data.createdAt);
  const triggeredTimes = getUtcLocalTimes(data.createdAt);

  // Timeline card variant uses a compact two-column layout
  if (variant === "timeline-card") {
    return (
      <div className="w-full" id="job-run-header">
        {/* Header row: Time + Button */}
        <div className="flex items-start justify-between gap-4">
          <div>
            {triggeredTimes ? (
              <>
                <p className="text-sm font-medium text-foreground">{triggeredTimes.utc} UTC</p>
                <p className="text-xs text-muted-foreground">{triggeredTimes.local} Local</p>
              </>
            ) : (
              <p className="text-sm font-medium text-foreground">{format(createdAt, "MMM d, HH:mm")}</p>
            )}
          </div>
          {rightContent && (
            <div className="shrink-0">
              {rightContent}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-border my-3" />

        {/* Summary */}
        {data.status === "COMPLETED" && data.investigation?.summary && (
          <div className="mb-3">
            <Markdown className="text-sm text-foreground [&_p]:m-0">
              {data.investigation.summary}
            </Markdown>
          </div>
        )}

        {/* Shimmer while investigation is in progress */}
        {data.status === "IN_PROGRESS" && !data.investigation?.summary && (
          <div className="mb-3">
            <Shimmer lines={2} seed={data.id} />
          </div>
        )}

        {/* Tags */}
        {data.status === "COMPLETED" && data.investigation && (
          <div className="flex flex-wrap gap-1.5">
            {onTagClick && data.investigation.category ? (
              <InteractiveAlertTag
                type="category"
                value={data.investigation.category}
                stats={tagStats?.categories[data.investigation.category]}
                isActive={activeFilters?.categories.has(data.investigation.category) || false}
                onClick={() => onTagClick("category", data.investigation!.category!)}
              />
            ) : data.investigation.category && (
              <span className="px-2 py-0.5 text-xs text-foreground bg-card border border-border rounded">
                {data.investigation.category}
              </span>
            )}
            {data.investigation.services && data.investigation.services.length > 0 &&
              data.investigation.services.map((service) => (
                service && onTagClick ? (
                  <InteractiveAlertTag
                    key={service}
                    type="service"
                    value={service}
                    stats={tagStats?.services[service]}
                    isActive={activeFilters?.services.has(service) || false}
                    onClick={() => onTagClick("service", service)}
                  />
                ) : service && (
                  <span key={service} className="px-2 py-0.5 text-xs text-foreground bg-card border border-border rounded">
                    {service}
                  </span>
                )
              ))
            }
          </div>
        )}
      </div>
    );
  }

  // Default layout - different styling for alert vs digest contexts
  const dateLabel = context === "alert" ? "Event Time:" : context === "digest" ? "Created At:" : null;

  return (
    <div className="overflow-hidden">
      <div className={cn("flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between")} id="job-run-header">
        <div className="flex items-center gap-2 min-w-0">
          <div className="space-y-2 min-w-0">
            {/* Title - same format as alert page title */}
            {showTitle && (
              <h1 className="text-xl font-medium text-foreground break-words">{title}</h1>
            )}

            {/* Event Timestamp / Created At */}
            <div className="text-sm text-muted-foreground">
              {dateLabel && <span className="text-muted-foreground">{dateLabel} </span>}
              {triggeredTimes ? (
                <span>
                  {triggeredTimes.utc} UTC{" "}
                  <span className="text-xs text-muted-foreground">({triggeredTimes.local} Local)</span>
                </span>
              ) : (
                format(createdAt, "MMM d, HH:mm")
              )}
            </div>

            {/* Alert Description - display clean description, hover shows full JSON */}
            {showTextMatch && (data.alertEvent?.alertDescription || data.outputJson?.input_str) && (
              <div className="text-sm text-muted-foreground flex min-w-0">
                <span className="text-muted-foreground shrink-0">Alert Description:&nbsp;</span>
                <TruncatedText
                  text={data.alertEvent?.alertDescription || data.outputJson?.input_str || ""}
                  hoverText={data.outputJson?.input_str || undefined}
                  maxLength={200}
                  renderer={(isTruncated, text) => (
                    <span className={cn("truncate", isTruncated && "cursor-help hover:underline")}>
                      {text}
                    </span>
                  )}
                />
              </div>
            )}

            {/* Summary - single line markdown with hover for full markdown */}
            {data.status === "COMPLETED" && data.investigation?.summary && (
              <div className="text-sm text-muted-foreground flex items-baseline">
                <span className="text-muted-foreground shrink-0">Summary:&nbsp;</span>
                <TruncatedMarkdown
                  text={data.investigation.summary}
                  className="flex-1 min-w-0"
                />
              </div>
            )}

            {/* Digest Summary */}
            {data.status === "COMPLETED" && data.digest?.summary && (
              <div className="text-sm text-muted-foreground flex items-baseline">
                <span className="text-muted-foreground shrink-0">Summary:&nbsp;</span>
                <TruncatedMarkdown
                  text={data.digest.summary}
                  className="flex-1 min-w-0"
                />
              </div>
            )}

            {/* Tags */}
            {data.status === "COMPLETED" && data.investigation && (
              <div className="text-sm text-muted-foreground">
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-muted-foreground whitespace-nowrap">Tags:</span>
                  {onTagClick && data.investigation.category ? (
                    <InteractiveAlertTag
                      type="category"
                      value={data.investigation.category}
                      stats={tagStats?.categories[data.investigation.category]}
                      isActive={activeFilters?.categories.has(data.investigation.category) || false}
                      onClick={() => onTagClick("category", data.investigation!.category!)}
                    />
                  ) : (
                    <span className="px-1.5 py-0.5 text-xs text-foreground bg-card border border-border rounded whitespace-nowrap overflow-hidden text-ellipsis max-w-xs">
                      Category: {data.investigation.category}
                    </span>
                  )}
                  {data.investigation.services && data.investigation.services.length > 0 &&
                    data.investigation.services.map((service) => (
                      service && onTagClick ? (
                        <InteractiveAlertTag
                          key={service}
                          type="service"
                          value={service}
                          stats={tagStats?.services[service]}
                          isActive={activeFilters?.services.has(service) || false}
                          onClick={() => onTagClick("service", service)}
                        />
                      ) : (
                        <span key={service} className="px-1.5 py-0.5 text-xs text-foreground bg-card border border-border rounded whitespace-nowrap overflow-hidden text-ellipsis max-w-xs">
                          Service: {service}
                        </span>
                      )
                    ))
                  }
                </div>
              </div>
            )}
          </div>
        </div>
        {rightContent && (
          <div className="shrink-0 w-full sm:w-auto">
            {rightContent}
          </div>
        )}
      </div>
    </div>
  );
}
